'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PartnershipCTAProps {
  className?: string;
}

const PartnershipCTA = ({ className = '' }: PartnershipCTAProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) return;

    try {
      const res = await fetch('/api/partnership-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit');
      }

      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <section
      data-header="dark"
      className={`py-8 bg-gradient-to-br from-secondary via-brand-earth to-secondary ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-1 sm:py-2 rounded-full mb-2 sm:mb-4">
            <Icon name="HandRaisedIcon" size={20} variant="solid" className="text-accent" />
            <span className="font-body text-xs sm:text-sm font-medium text-white">
              Partnership Opportunities
            </span>
          </div>

          <h2 className="font-headline text-3xl lg:text-5xl font-semibold text-white mb-2 sm:mb-4">
            Join <span className="text-accent">Africa's Transformation</span> Journey
          </h2>

          <p className="font-body text-base sm:text-lg lg:text-xl text-gray-200 mb-2 sm:mb-4 leading-relaxed">
            Partner with us to drive sustainable economic development through patient capital,
            systematic methodology, and authentic collaboration. Multiple engagement pathways
            available for institutional investors, government partners, and strategic collaborators.
          </p>

          <div className="backdrop-blur-sm rounded-lg p-4 sm:p-6 mb-2 sm:mb-4">
            <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-6">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email for partnership information"
                  required
                  className="flex-1 px-6 py-2 bg-white text-foreground font-body text-base rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                />

                <button
                  type="submit"
                  disabled={isSubmitted}
                  className="px-8 py-2 bg-accent text-accent-foreground font-body font-semibold text-base rounded-md hover:bg-accent/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitted ? (
                    <span className="flex items-center justify-center">
                      <Icon name="CheckCircleIcon" size={20} variant="solid" className="mr-2" />
                      Submitted
                    </span>
                  ) : (
                    'Get Started'
                  )}
                </button>
              </div>

              {isSubmitted && (
                <p className="mt-4 font-body text-sm text-white">
                  Thank you! Our partnership team will contact you within 48 hours.
                </p>
              )}
              {error && <p className="mt-2 font-body text-sm text-red-400">{error}</p>}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipCTA;
